// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the deck, and the one piece of arithmetic the whole game rests on.
//
// A card is `suit * 8 + rank`, rank 1..5, so `card >> 3` is the suit and `card & 7` is the rank, and
// 0 is not a legal card. That last part is load-bearing rather than tidy: 0 is the wire value for
// A CARD YOU ARE NOT ALLOWED TO SEE, which in this game is most of what you are holding. Every
// redacted view is an array of card codes with your own entries replaced by 0, so "unknown" has to be
// a value no real card can ever take.

/** How many of each rank exist in an ordinary suit — ones are common, the five is unique. */
export const RANK_COPIES: readonly number[] = [0, 3, 2, 2, 2, 1];

/** The highest a plume can be raised. Also the number of ranks. */
export const TOP = 5;

export const card = (suit: number, rank: number): number => suit * 8 + rank;
export const suitOf = (c: number): number => c >> 3;
export const rankOf = (c: number): number => c & 7;

/** The unknown-card sentinel. Never a legal card, because rank 0 does not exist. */
export const HIDDEN = 0;

export interface SuitSpec {
  id: number;
  name: string;
  /** Distinct glyph per suit, so the game is playable with no colour vision at all. */
  glyph: string;
  /**
   * One copy of each rank instead of 3/2/2/2/1. Every card of such a suit is irreplaceable, which
   * turns the whole discard decision from "what can I afford to lose" into "I can afford nothing".
   */
  singleton?: boolean;
  /**
   * Touched by EVERY colour hint, not just its own. A card that has been pointed at as two different
   * colours can only be this suit — which is the deduction the Prism mode exists to create.
   */
  wild?: boolean;
}

/**
 * The five ordinary colours, plus the two possible sixth suits. Ids are stable and are what travels
 * on the wire; the names and glyphs are presentation.
 */
export const SUITS: readonly SuitSpec[] = [
  { id: 0, name: 'Amber', glyph: '▲' },
  { id: 1, name: 'Cyan', glyph: '●' },
  { id: 2, name: 'Rose', glyph: '◆' },
  { id: 3, name: 'Verdant', glyph: '■' },
  { id: 4, name: 'Violet', glyph: '★' },
  { id: 5, name: 'Prism', glyph: '✳', wild: true },
  { id: 5, name: 'Onyx', glyph: '⬢', singleton: true },
];

export const PRISM = SUITS[5];
export const ONYX = SUITS[6];

/** The suit line-up for a mode: always the five colours, optionally one more. */
export function suitsFor(sixth: SuitSpec | null): SuitSpec[] {
  const base = SUITS.slice(0, 5);
  return sixth ? [...base, sixth] : [...base];
}

/** How many copies of `rank` this suit holds. */
export function copiesOf(spec: SuitSpec, rank: number): number {
  return spec.singleton ? 1 : RANK_COPIES[rank];
}

/** The whole deck, in sorted order. Shuffling is somebody else's job. */
export function buildDeck(suits: readonly SuitSpec[]): number[] {
  const out: number[] = [];
  for (const s of suits) {
    for (let rank = 1; rank <= TOP; rank++) {
      for (let n = copiesOf(s, rank); n > 0; n--) out.push(card(s.id, rank));
    }
  }
  return out;
}

/**
 * Cards in hand. Fewer hands means you can hold more of them, which is the only reason a two-player
 * game is playable at all: with four cards each and eight sparks between you there is not enough
 * information in the room to finish a plume.
 */
export function handSize(players: number): number {
  return players <= 3 ? 5 : 4;
}

/**
 * Does a colour hint for `colour` touch this card? A wild suit answers yes to every colour, which is
 * the entire mechanical content of Prism mode and the reason this is a function rather than `===`.
 */
export function touchedByColour(c: number, colour: number, suits: readonly SuitSpec[]): boolean {
  const s = suitOf(c);
  if (s === colour) return true;
  const spec = suits.find((x) => x.id === s);
  return spec?.wild === true;
}

/** The maximum a crew could ever reach in this line-up. */
export const maxScore = (suits: readonly SuitSpec[]): number => suits.length * TOP;
